const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

class ApiService {
  // Flag to prevent multiple session-expired events
  _sessionExpiredEmitted = false;

  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;

    const config = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Don't set Content-Type for FormData (let browser set it with boundary)
    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    const response = await fetch(url, config);

    if (!response.ok) {
      // Check for session expiration (401 Unauthorized)
      if (response.status === 401) {
        // Emit session-expired event only once
        if (!this._sessionExpiredEmitted) {
          this._sessionExpiredEmitted = true;
          window.dispatchEvent(new CustomEvent('session-expired'));
        }
        throw new Error('Session expired');
      }

      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Reset the session expired flag (called after successful login)
  resetSessionExpiredFlag() {
    this._sessionExpiredEmitted = false;
  }

  // Agent CRUD
  async getAgents() {
    return this.request('/agents');
  }

  async getAgent(agentId) {
    return this.request(`/agents/${agentId}`);
  }

  async createAgent(data) {
    return this.request('/agents', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAgent(agentId) {
    return this.request(`/agents/${agentId}`, {
      method: 'DELETE',
    });
  }

  // Step 1: Name
  async updateAgentName(agentId, data) {
    return this.request(`/agents/${agentId}/name`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Step 2: Behavior
  async updateAgentBehavior(agentId, data) {
    return this.request(`/agents/${agentId}/behavior`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // AI Text Formatting
  async formatWithAI(text, fieldType, context = {}) {
    return this.request('/agents/format-with-ai', {
      method: 'POST',
      body: JSON.stringify({
        text,
        field_type: fieldType,
        context,
      }),
    });
  }

  // Step 3: Knowledge
  async getKnowledgeFiles(agentId) {
    return this.request(`/agents/${agentId}/knowledge`);
  }

  async uploadKnowledgeFile(agentId, file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request(`/agents/${agentId}/knowledge`, {
      method: 'POST',
      body: formData,
    });
  }

  async deleteKnowledgeFile(agentId, fileId) {
    return this.request(`/agents/${agentId}/knowledge/${fileId}`, {
      method: 'DELETE',
    });
  }

  async retrainKnowledge(agentId) {
    return this.request(`/agents/${agentId}/retrain-knowledge`, {
      method: 'POST',
    });
  }

  // Step 4: Model
  async getAvailableModels() {
    return this.request('/agents/models');
  }

  async updateAgentModel(agentId, data) {
    return this.request(`/agents/${agentId}/model`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Step 5: Features
  async updateAgentFeatures(agentId, data) {
    return this.request(`/agents/${agentId}/features`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Step 6: Training
  async trainAgent(agentId) {
    return this.request(`/agents/${agentId}/train`, {
      method: 'POST',
    });
  }

  async getTrainingStatus(agentId) {
    return this.request(`/agents/${agentId}/training-status`);
  }

  // Step 7: Test Chat
  async chatWithAgent(agentId, message) {
    return this.request(`/agents/${agentId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  // Simulator History
  async getSimulatorHistory(agentId) {
    return this.request(`/agents/${agentId}/simulator/history`);
  }

  async clearSimulatorHistory(agentId) {
    return this.request(`/agents/${agentId}/simulator/clear`, {
      method: 'POST',
    });
  }

  // Step Progress
  async updateStepProgress(agentId, currentStep) {
    return this.request(`/agents/${agentId}/step`, {
      method: 'PUT',
      body: JSON.stringify({ current_step: currentStep }),
    });
  }

  // ==================== WhatsApp Integration ====================

  /**
   * Start the WhatsApp pairing process for an agent.
   * Creates a WasenderAPI session and returns QR code data.
   * @param {string} agentId - The agent ID
   * @param {string} phoneNumber - Phone number in international format (e.g., +1234567890)
   */
  async pairWhatsApp(agentId, phoneNumber) {
    return this.request(`/whatsapp/agents/${agentId}/pair`, {
      method: 'POST',
      body: JSON.stringify({ phone_number: phoneNumber }),
    });
  }

  /**
   * Get the WhatsApp connection status for an agent.
   * Also auto-captures the session API key when connected.
   */
  async getWhatsAppStatus(agentId) {
    return this.request(`/whatsapp/agents/${agentId}/status`);
  }

  /**
   * Disconnect WhatsApp from an agent.
   */
  async disconnectWhatsApp(agentId) {
    return this.request(`/whatsapp/agents/${agentId}/disconnect`, {
      method: 'POST',
    });
  }

  /**
   * Refresh the QR code for an existing WhatsApp session.
   * Does NOT create a new session - just requests a fresh QR code.
   */
  async refreshWhatsAppQR(agentId) {
    return this.request(`/whatsapp/agents/${agentId}/refresh-qr`, {
      method: 'POST',
    });
  }

  /**
   * Save the session API key for an agent (manual fallback).
   */
  async saveWhatsAppApiKey(agentId, apiKey) {
    return this.request(`/whatsapp/agents/${agentId}/api-key`, {
      method: 'POST',
      body: JSON.stringify({ api_key: apiKey }),
    });
  }

  // ==================== Step 10: Go Live ====================

  /**
   * Launch/activate an agent.
   * @param {string} agentId - The agent ID
   * @param {object} data - Launch settings
   * @param {string} data.respond_to - 'all' or 'whitelist'
   * @param {string[]} data.whitelist - Phone numbers to whitelist (if respond_to is 'whitelist')
   */
  async launchAgent(agentId, data) {
    return this.request(`/agents/${agentId}/launch`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Deactivate/pause an agent.
   */
  async deactivateAgent(agentId) {
    return this.request(`/agents/${agentId}/deactivate`, {
      method: 'POST',
    });
  }

  // ==================== Dashboard - Conversations ====================

  /**
   * Get all conversations for an agent.
   * @param {string} agentId - The agent ID
   * @param {object} options - Query options
   * @param {number} options.limit - Max conversations to return
   * @param {string} options.search - Search term for phone/name
   */
  async getConversations(agentId, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.search) params.append('search', options.search);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/dashboard/agents/${agentId}/conversations${query}`);
  }

  /**
   * Get a single conversation with messages.
   * @param {string} agentId - The agent ID
   * @param {string} conversationId - The conversation ID
   * @param {number} limit - Max messages to return
   */
  async getConversation(agentId, conversationId, limit = 50) {
    return this.request(`/dashboard/agents/${agentId}/conversations/${conversationId}?limit=${limit}`);
  }

  /**
   * Send a message to a conversation via WhatsApp.
   * @param {string} agentId - The agent ID
   * @param {string} conversationId - The conversation ID
   * @param {string} message - The message text to send
   */
  async sendMessageToConversation(agentId, conversationId, message) {
    return this.request(`/dashboard/agents/${agentId}/conversations/${conversationId}/send`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  /**
   * Update AI paused status for a conversation.
   * @param {string} agentId - The agent ID
   * @param {string} conversationId - The conversation ID
   * @param {boolean} aiPaused - Whether AI should be paused for this conversation
   */
  async updateConversationAIPaused(agentId, conversationId, aiPaused) {
    return this.request(`/dashboard/agents/${agentId}/conversations/${conversationId}/ai-pause`, {
      method: 'PUT',
      body: JSON.stringify({ aiPaused }),
    });
  }

  /**
   * Mark conversation as reviewed (clear needsHumanAttention flag).
   * @param {string} agentId - The agent ID
   * @param {string} conversationId - The conversation ID
   */
  async markConversationReviewed(agentId, conversationId) {
    return this.request(`/dashboard/agents/${agentId}/conversations/${conversationId}/mark-reviewed`, {
      method: 'PUT',
    });
  }

  // ==================== Dashboard - Leads ====================

  /**
   * Get all leads for an agent.
   * @param {string} agentId - The agent ID
   * @param {object} options - Query options
   * @param {number} options.limit - Max leads to return
   * @param {string} options.search - Search term
   * @param {string} options.tags - Comma-separated tags filter
   * @param {string} options.sort - Sort order ('newest', 'oldest', 'name')
   */
  async getLeads(agentId, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.search) params.append('search', options.search);
    if (options.tags) params.append('tags', options.tags);
    if (options.sort) params.append('sort', options.sort);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/dashboard/agents/${agentId}/leads${query}`);
  }

  /**
   * Create a new lead manually.
   * @param {string} agentId - The agent ID
   * @param {object} leadData - Lead data (name, phone, email, tags, notes)
   */
  async createLead(agentId, leadData) {
    return this.request(`/dashboard/agents/${agentId}/leads`, {
      method: 'POST',
      body: JSON.stringify(leadData),
    });
  }

  /**
   * Update a lead.
   * @param {string} agentId - The agent ID
   * @param {string} leadId - The lead ID
   * @param {object} updates - Fields to update
   */
  async updateLead(agentId, leadId, updates) {
    return this.request(`/dashboard/agents/${agentId}/leads/${leadId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete a lead.
   * @param {string} agentId - The agent ID
   * @param {string} leadId - The lead ID
   */
  async deleteLead(agentId, leadId) {
    return this.request(`/dashboard/agents/${agentId}/leads/${leadId}`, {
      method: 'DELETE',
    });
  }

  // ==================== Dashboard - Appointments ====================

  /**
   * Get appointments for an agent.
   * @param {string} agentId - The agent ID
   * @param {object} options - Query options
   * @param {number} options.limit - Max number of appointments
   * @param {string} options.search - Search by customer name/phone
   * @param {string} options.status - Filter by status
   * @param {string} options.dateFrom - Filter from date (YYYY-MM-DD)
   * @param {string} options.dateTo - Filter to date (YYYY-MM-DD)
   * @param {string} options.sort - Sort order ('upcoming', 'recent')
   */
  async getAppointments(agentId, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.search) params.append('search', options.search);
    if (options.status) params.append('status', options.status);
    if (options.dateFrom) params.append('date_from', options.dateFrom);
    if (options.dateTo) params.append('date_to', options.dateTo);
    if (options.sort) params.append('sort', options.sort);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/dashboard/agents/${agentId}/appointments${query}`);
  }

  /**
   * Get a single appointment by ID.
   * @param {string} agentId - The agent ID
   * @param {string} appointmentId - The appointment ID
   */
  async getAppointment(agentId, appointmentId) {
    return this.request(`/dashboard/agents/${agentId}/appointments/${appointmentId}`);
  }

  /**
   * Create a new appointment manually.
   * @param {string} agentId - The agent ID
   * @param {object} appointmentData - Appointment data
   */
  async createAppointment(agentId, appointmentData) {
    return this.request(`/dashboard/agents/${agentId}/appointments`, {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  }

  /**
   * Update an appointment.
   * @param {string} agentId - The agent ID
   * @param {string} appointmentId - The appointment ID
   * @param {object} updates - Fields to update
   */
  async updateAppointment(agentId, appointmentId, updates) {
    return this.request(`/dashboard/agents/${agentId}/appointments/${appointmentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Update appointment status.
   * @param {string} agentId - The agent ID
   * @param {string} appointmentId - The appointment ID
   * @param {string} status - New status (pending, confirmed, completed, cancelled, no_show)
   * @param {string} reason - Optional cancellation reason
   */
  async updateAppointmentStatus(agentId, appointmentId, status, reason = null) {
    const body = { status };
    if (reason) body.reason = reason;
    return this.request(`/dashboard/agents/${agentId}/appointments/${appointmentId}/status`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  /**
   * Delete an appointment.
   * @param {string} agentId - The agent ID
   * @param {string} appointmentId - The appointment ID
   */
  async deleteAppointment(agentId, appointmentId) {
    return this.request(`/dashboard/agents/${agentId}/appointments/${appointmentId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get available time slots for a date.
   * @param {string} agentId - The agent ID
   * @param {string} date - The date (YYYY-MM-DD)
   */
  async getAvailableSlots(agentId, date) {
    return this.request(`/dashboard/agents/${agentId}/appointments/availability?date=${date}`);
  }

  // ==================== Dashboard - Emails ====================

  /**
   * Get emails for an agent.
   * @param {string} agentId - The agent ID
   * @param {object} options - Query options (search, status, limit)
   */
  async getEmails(agentId, options = {}) {
    const params = new URLSearchParams();
    if (options.search) params.append('search', options.search);
    if (options.status) params.append('status', options.status);
    if (options.limit) params.append('limit', options.limit);
    const query = params.toString();
    return this.request(`/dashboard/agents/${agentId}/emails${query ? `?${query}` : ''}`);
  }

  /**
   * Get a single email by ID.
   * @param {string} agentId - The agent ID
   * @param {string} emailId - The email ID
   */
  async getEmail(agentId, emailId) {
    return this.request(`/dashboard/agents/${agentId}/emails/${emailId}`);
  }

  /**
   * Delete an email record.
   * @param {string} agentId - The agent ID
   * @param {string} emailId - The email ID
   */
  async deleteEmail(agentId, emailId) {
    return this.request(`/dashboard/agents/${agentId}/emails/${emailId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Send an email manually from the dashboard.
   * @param {string} agentId - The agent ID
   * @param {object} emailData - Email data (recipient_email, recipient_name, subject, body)
   */
  async sendEmail(agentId, emailData) {
    return this.request(`/dashboard/agents/${agentId}/emails/send`, {
      method: 'POST',
      body: JSON.stringify(emailData),
    });
  }

  /**
   * Test Gmail connection with configured credentials.
   * @param {string} agentId - The agent ID
   */
  async testEmailConnection(agentId) {
    return this.request(`/dashboard/agents/${agentId}/emails/test`, {
      method: 'POST',
    });
  }

  /**
   * Get email statistics for an agent.
   * @param {string} agentId - The agent ID
   */
  async getEmailStats(agentId) {
    return this.request(`/dashboard/agents/${agentId}/emails/stats`);
  }

  // ==================== Dashboard - Analytics ====================

  /**
   * Get analytics data for an agent.
   * @param {string} agentId - The agent ID
   * @param {string} range - Time range ('24h', '7d', '30d', '90d')
   */
  async getAnalytics(agentId, range = '7d') {
    return this.request(`/dashboard/agents/${agentId}/analytics?range=${range}`);
  }

  // ==================== Dashboard - Settings ====================

  /**
   * Update agent settings (comprehensive update).
   * @param {string} agentId - The agent ID
   * @param {object} settings - Settings to update
   */
  async updateAgentSettings(agentId, settings) {
    return this.request(`/dashboard/agents/${agentId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // ==================== Dashboard - Status & Control ====================

  /**
   * Get agent status including WhatsApp connection and stats.
   * @param {string} agentId - The agent ID
   */
  async getAgentStatus(agentId) {
    return this.request(`/dashboard/agents/${agentId}/status`);
  }

  /**
   * Pause an agent (stop responding to messages).
   * @param {string} agentId - The agent ID
   */
  async pauseAgent(agentId) {
    return this.request(`/dashboard/agents/${agentId}/pause`, {
      method: 'POST',
    });
  }

  /**
   * Resume a paused agent.
   * @param {string} agentId - The agent ID
   */
  async resumeAgent(agentId) {
    return this.request(`/dashboard/agents/${agentId}/resume`, {
      method: 'POST',
    });
  }

  // ==================== Feedback ====================

  /**
   * Submit user feedback with optional file attachments.
   * @param {Object} data - Feedback data
   * @param {number} data.rating - Star rating 1-5
   * @param {string} data.feedbackType - Type: 'bug', 'feature', 'ux', 'general'
   * @param {string} data.message - Feedback message
   * @param {File[]} data.attachments - Array of file attachments
   */
  async submitFeedback(data) {
    const formData = new FormData();
    formData.append('rating', data.rating || 0);
    formData.append('feedbackType', data.feedbackType || 'general');
    formData.append('message', data.message || '');

    // Add file attachments
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((attachment) => {
        if (attachment.file) {
          formData.append('attachments', attachment.file);
        }
      });
    }

    return this.request('/dashboard/feedback', {
      method: 'POST',
      body: formData,
    });
  }

  /**
   * Get all feedback submitted by the current user.
   */
  async getUserFeedback() {
    return this.request('/dashboard/feedback');
  }

  // ==================== Custom Agent Requests ====================

  /**
   * Submit a custom agent request.
   * @param {Object} data - Request data
   * @param {string} data.name - Requester's name
   * @param {string} data.email - Requester's email
   * @param {string} data.phone - Requester's phone (optional)
   * @param {string} data.businessName - Business name (optional)
   * @param {string} data.description - Description of the custom agent needed
   */
  async submitCustomAgentRequest(data) {
    return this.request('/dashboard/custom-agent-request', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== Documentation ====================

  /**
   * Get public documentation structure.
   * @returns {Promise<{sections: Array}>} Documentation sections with articles
   */
  async getDocs() {
    return this.request('/dashboard/docs');
  }
}

export const api = new ApiService();
export default api;